import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Alert, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEdit, 
  faPercentage, 
  faDollarSign, 
  faShield,
  faFileInvoiceDollar,
  faHandshake,
  faUserTie,
  faCalculator,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import BrokerApiService from '../../services/brokerApiService';
import toast from 'react-hot-toast';

interface EditAgentModalProps {
  show: boolean;
  onHide: () => void;
  agent: any;
  brokerageId: string;
  onAgentUpdated: () => void;
}

interface CommissionFormData {
  commission_split_agent: number;
  commission_split_broker: number;
  eo_fee: number;
  transaction_fee: number;
  has_partner_splits: boolean;
  partner_split_percentage: number;
  partner_agent_id: string;
  monthly_fee: number;
}

const EditAgentModal: React.FC<EditAgentModalProps> = ({
  show,
  onHide,
  agent,
  brokerageId,
  onAgentUpdated
}) => {
  const [formData, setFormData] = useState<CommissionFormData>({
    commission_split_agent: 80,
    commission_split_broker: 20,
    eo_fee: 0,
    transaction_fee: 0,
    has_partner_splits: false,
    partner_split_percentage: 0,
    partner_agent_id: '',
    monthly_fee: 0
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (agent) {
      setFormData({
        commission_split_agent: agent.commissionSplitAgent || 80,
        commission_split_broker: agent.commissionSplitBroker || 20,
        eo_fee: agent.eoFee || 0,
        transaction_fee: agent.transactionFee || 0,
        has_partner_splits: agent.hasPartnerSplits || false,
        partner_split_percentage: agent.partnerSplitPercentage || 0,
        partner_agent_id: agent.partnerAgentId || '',
        monthly_fee: agent.monthlyFee || 0
      });
    }
  }, [agent]);

  const validateCommissionSplits = () => {
    const total = formData.commission_split_agent + formData.commission_split_broker;
    if (total !== 100) {
      setValidationError(`Commission splits must equal 100%. Current total: ${total}%`);
      return false;
    }
    
    if (formData.has_partner_splits && (formData.partner_split_percentage < 0 || formData.partner_split_percentage > 100)) {
      setValidationError('Partner split percentage must be between 0 and 100%');
      return false;
    }
    
    setValidationError(null);
    return true;
  };

  const handleInputChange = (field: keyof CommissionFormData, value: any) => {
    const updatedData = { ...formData, [field]: value };
    
    // Auto-calculate broker split when agent split changes
    if (field === 'commission_split_agent') {
      updatedData.commission_split_broker = 100 - value;
    } else if (field === 'commission_split_broker') {
      updatedData.commission_split_agent = 100 - value;
    }
    
    setFormData(updatedData);
    setValidationError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateCommissionSplits()) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Call API to update agent commission settings
      const response = await BrokerApiService.updateAgentCommissionSettings(
        agent.id,
        formData
      );
      
      if (response.success) {
        toast.success('Agent commission settings updated successfully!');
        // Call onAgentUpdated first to refresh the list
        await onAgentUpdated();
        // Then close the modal
        onHide();
      } else {
        throw new Error(response.message || 'Failed to update agent settings');
      }
    } catch (err) {
      console.error('Error updating agent:', err);
      setError(err instanceof Error ? err.message : 'Failed to update agent settings');
      toast.error('Failed to update agent settings');
    } finally {
      setLoading(false);
    }
  };

  const calculateNetCommission = () => {
    const sampleGrossCommission = 10000; // $10,000 sample commission
    const agentGross = (sampleGrossCommission * formData.commission_split_agent) / 100;
    const partnerSplit = formData.has_partner_splits 
      ? (agentGross * formData.partner_split_percentage) / 100 
      : 0;
    const netCommission = agentGross - formData.eo_fee - formData.transaction_fee - partnerSplit;
    
    return {
      gross: agentGross,
      partnerSplit,
      net: netCommission
    };
  };

  const commissionExample = calculateNetCommission();

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header 
        closeButton
        style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
        }}
      >
        <Modal.Title className="d-flex align-items-center">
          <FontAwesomeIcon icon={faEdit} className="me-2 text-primary" />
          Edit Agent Commission Settings
        </Modal.Title>
      </Modal.Header>
      
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {agent && (
            <div>
              <div className="mb-4 p-3 bg-light rounded">
                <h6 className="mb-2">
                  <FontAwesomeIcon icon={faUserTie} className="me-2 text-primary" />
                  {agent.name}
                </h6>
                <small className="text-muted">{agent.email}</small>
              </div>

              {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              
              {validationError && (
                <Alert variant="warning">
                  <FontAwesomeIcon icon={faCalculator} className="me-2" />
                  {validationError}
                </Alert>
              )}

              {/* Commission Splits */}
              <h5 className="mb-3">
                <FontAwesomeIcon icon={faPercentage} className="me-2 text-primary" />
                Commission Splits
              </h5>
              
              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Agent Split (%)</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        min="0"
                        max="100"
                        value={formData.commission_split_agent}
                        onChange={(e) => handleInputChange('commission_split_agent', parseInt(e.target.value) || 0)}
                        required
                      />
                      <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Broker Split (%)</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        min="0"
                        max="100"
                        value={formData.commission_split_broker}
                        onChange={(e) => handleInputChange('commission_split_broker', parseInt(e.target.value) || 0)}
                        required
                      />
                      <InputGroup.Text>%</InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              {/* Fees */}
              <h5 className="mb-3">
                <FontAwesomeIcon icon={faDollarSign} className="me-2 text-success" />
                Fees & Deductions
              </h5>
              
              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FontAwesomeIcon icon={faShield} className="me-1 text-warning" />
                      E&O Insurance Fee
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text>$</InputGroup.Text>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.eo_fee}
                        onChange={(e) => handleInputChange('eo_fee', parseFloat(e.target.value) || 0)}
                        required
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FontAwesomeIcon icon={faFileInvoiceDollar} className="me-1 text-info" />
                      Transaction Fee
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text>$</InputGroup.Text>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.transaction_fee}
                        onChange={(e) => handleInputChange('transaction_fee', parseFloat(e.target.value) || 0)}
                        required
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              {/* Monthly Fee */}
              <Row className="mb-4">
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FontAwesomeIcon icon={faDollarSign} className="me-1 text-primary" />
                      Monthly Agent Fee
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text>$</InputGroup.Text>
                      <Form.Control
                        type="number"
                        min="0"
                        step="1"
                        value={formData.monthly_fee}
                        onChange={(e) => handleInputChange('monthly_fee', parseFloat(e.target.value) || 0)}
                        required
                      />
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Total monthly fee charged to agent. Brokerage keeps ${(formData.monthly_fee - 70) > 0 ? (formData.monthly_fee - 70).toFixed(2) : '0'} after $70 KairosIQ platform fee.
                      {formData.monthly_fee > 0 && formData.monthly_fee < 70 && (
                        <span className="text-warning d-block mt-1">
                          <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                          Warning: Fee is less than $70 platform cost
                        </span>
                      )}
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              {/* Partner Splits */}
              <h5 className="mb-3">
                <FontAwesomeIcon icon={faHandshake} className="me-2 text-info" />
                Partner Splits
              </h5>
              
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="This agent has partner split arrangements"
                  checked={formData.has_partner_splits}
                  onChange={(e) => handleInputChange('has_partner_splits', e.target.checked)}
                />
              </Form.Group>
              
              {formData.has_partner_splits && (
                <Row className="mb-4">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Partner Split Percentage</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          min="0"
                          max="100"
                          value={formData.partner_split_percentage}
                          onChange={(e) => handleInputChange('partner_split_percentage', parseFloat(e.target.value) || 0)}
                        />
                        <InputGroup.Text>%</InputGroup.Text>
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Percentage of agent's commission to share with partner
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {/* Commission Calculator Example */}
              <div className="mt-4 p-3 bg-light rounded">
                <h6 className="mb-3">
                  <FontAwesomeIcon icon={faCalculator} className="me-2 text-primary" />
                  Commission Example (on $10,000 gross commission)
                </h6>
                <Row>
                  <Col md={6}>
                    <small className="d-block">Agent Gross: ${commissionExample.gross.toFixed(2)}</small>
                    <small className="d-block">- E&O Fee: ${formData.eo_fee.toFixed(2)}</small>
                    <small className="d-block">- Transaction Fee: ${formData.transaction_fee.toFixed(2)}</small>
                    {formData.has_partner_splits && (
                      <small className="d-block">- Partner Split: ${commissionExample.partnerSplit.toFixed(2)}</small>
                    )}
                  </Col>
                  <Col md={6}>
                    <div className="text-end">
                      <strong className="text-success">Net to Agent: ${commissionExample.net.toFixed(2)}</strong>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          )}
        </Modal.Body>
        
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit" 
            disabled={loading || !!validationError}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faEdit} className="me-2" />
                Save Changes
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditAgentModal;